import { dayjs } from "@outschool/time";
import React from "react";

import { BubbleMessageData } from "./types";

export function isSameGroup(
  a: BubbleMessageData,
  b: BubbleMessageData,
): boolean {
  return (
    a.sender.uid === b.sender.uid &&
    dayjs(a.sentAt).format("YYYY-MM-DD HH:mm") ===
      dayjs(b.sentAt).format("YYYY-MM-DD HH:mm")
  );
}

interface UseConversationTimelineProps {
  messages: BubbleMessageData[];
  searchQuery: string;
  highlightMessageUid?: string | null;
  conversationKey: string;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}

export function useConversationTimeline({
  messages,
  searchQuery,
  highlightMessageUid,
  conversationKey,
  scrollContainerRef,
}: UseConversationTimelineProps) {
  const matchingUids = React.useMemo(() => {
    if (searchQuery) {
      const lower = searchQuery.toLowerCase();
      return new Set(
        messages
          .filter(m => m.messageContent.text?.toLowerCase().includes(lower))
          .map(m => m.uid),
      );
    }
    if (highlightMessageUid) {
      return new Set([highlightMessageUid]);
    }
    return new Set<string>();
  }, [messages, searchQuery, highlightMessageUid]);

  const firstMatchUid = React.useMemo(() => {
    return messages.find(m => matchingUids.has(m.uid))?.uid ?? null;
  }, [messages, matchingUids]);

  const firstMatchScrolled = React.useRef(false);

  React.useEffect(() => {
    firstMatchScrolled.current = false;
  }, [conversationKey]);

  const firstMatchRef = React.useCallback((node: HTMLDivElement | null) => {
    if (node && !firstMatchScrolled.current) {
      firstMatchScrolled.current = true;
      node.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, []);

  React.useEffect(() => {
    if (firstMatchUid) {
      return;
    }
    const container = scrollContainerRef.current;
    if (!container || messages.length === 0) {
      return;
    }

    const scrollToBottom = () => {
      container.scrollTop = container.scrollHeight;
    };

    scrollToBottom();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(scrollToBottom);
    for (const child of Array.from(container.children)) {
      resizeObserver.observe(child);
    }

    return () => resizeObserver.disconnect();
  }, [firstMatchUid, messages.length, scrollContainerRef]);

  return {
    matchingUids,
    firstMatchUid,
    firstMatchRef,
  };
}
